import { useCallback, useMemo, useState } from "react";

/**
 * Iter56 — Reusable multi-select selection state for list pages.
 *
 * Callers pass the CURRENTLY VISIBLE rows (after filters/pagination), and the
 * hook manages a Set of selected ids. Toggling "select all" only affects the
 * currently-visible rows so users can't accidentally select records they don't
 * see.
 *
 * Usage:
 *   const bulk = useBulkSelection(visibleRows, (r) => r.id);
 *   bulk.selectedIds        // Set<string>
 *   bulk.selectedCount      // number
 *   bulk.isSelected(id)     // bool
 *   bulk.toggle(id)         // toggle one
 *   bulk.toggleAll()        // header checkbox
 *   bulk.clear()            // deselect all
 *   bulk.allVisibleSelected // bool for header state
 */
export default function useBulkSelection(rows, idOf = (r) => r.id) {
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const visibleIds = useMemo(() => (rows || []).map(idOf), [rows, idOf]);

    const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

    const toggle = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clear = useCallback(() => setSelectedIds(new Set()), []);

    const allVisibleSelected = useMemo(() => {
        if (!visibleIds.length) return false;
        return visibleIds.every((id) => selectedIds.has(id));
    }, [visibleIds, selectedIds]);

    const someVisibleSelected = useMemo(
        () => visibleIds.some((id) => selectedIds.has(id)),
        [visibleIds, selectedIds],
    );

    const toggleAll = useCallback(() => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const everySelected = visibleIds.every((id) => next.has(id));
            if (everySelected) {
                for (const id of visibleIds) next.delete(id);
            } else {
                for (const id of visibleIds) next.add(id);
            }
            return next;
        });
    }, [visibleIds]);

    return {
        selectedIds,
        selectedCount: selectedIds.size,
        isSelected,
        toggle,
        toggleAll,
        clear,
        allVisibleSelected,
        someVisibleSelected,
    };
}
